/**
 * Dropdown option lists for the ERP Outbound Report filters.
 *
 * Legal Entity options are loaded live from `dbov2/legal-entity/many`
 * (see ErpOutboundLookupService). The three lists below have no dedicated
 * lookup endpoint yet — the values are dynamic strings written by the ERP
 * response payload. They are seeded here from the known backend values and
 * are intended to be adjusted (or wired to a distinct-values endpoint) once
 * the outbound API exposes them.
 */

export interface ErpOutboundOption {
  value: string;
  label: string;
}

/** Seeded from Application.ErpResponses.Constants.ErpQueueConstants. */
export const ERP_OUTBOUND_RESPONSE_TYPE_OPTIONS: ErpOutboundOption[] = [
  {value: 'UpdateInvoiceDetails', label: 'Invoice'},
  {value: 'UpdateTrailerID', label: 'Trailer'},
];

export const ERP_OUTBOUND_RESULT_OPTIONS: ErpOutboundOption[] = [
  {value: 'Invoiced', label: 'Invoiced'},
  {value: 'OK', label: 'OK'},
  {value: 'InterpretationError', label: 'Failed'},
  {value: 'InvoiceError', label: 'Error'},
  {value: 'TicketForVoidNotFound', label: 'Void Ticket Not Found'},
  {value: 'PENDING', label: 'Pending'}
];

/**
 * Semantic colouring for the Result chip — matches on the value's meaning
 * rather than an exact string, so it stays correct even if the exact Result
 * labels differ from the seeded options above.
 */
/**
 * Whether a row represents an error state — used to surface a warning indicator
 * next to the Result. Mirrors the error branch of {@link erpOutboundResultChipStyle}
 * and also treats any present error description as an error worth flagging.
 */
export function isErpOutboundError(result: string | null, errorDescription?: string | null): boolean {
  if (errorDescription && errorDescription.trim()) return true;
  if (!result) return false;

  const v = result.toLowerCase();
  return v.includes('fail') || v.includes('error') || v.includes('notfound');
}

export function erpOutboundResultChipStyle(value: string | null): Record<string, string> {
  if (!value) return {};

  const v = value.toLowerCase();
  let background = '#eceff1';
  let color = '#37474f';

  if (v.includes('success') || v.includes('complete') || v === 'ok' || v.includes('processed')) {
    background = '#e6f4ea';
    color = '#1e7e34';
  } else if (v.includes('fail') || v.includes('error') || v.includes('reject')) {
    background = '#fdecea';
    color = '#c62828';
  } else if (v.includes('warn') || v.includes('partial') || v.includes('pending') || v.includes('retry')) {
    background = '#fff8e1';
    color = '#8a6d00';
  }

  return {
    'background-color': background,
    color,
    'font-weight': '600',
    'border-radius': '12px',
    padding: '2px 12px',
    'text-align': 'center',
    'white-space': 'nowrap',
  };
}
